// client/src/components/forms/FormEditor.jsx
//
// /:tenant/forms/:formId — one form: build (fields, reorder, types,
// required, live preview), settings (consent, success, redirect, push),
// share (hosted URL, iframe snippet, event-page section, webhook) and
// submissions (table + CSV). Growth Phase 4, feature_key "forms".
//
// Field definitions use the ticketing registration-field vocabulary
// (field_key / label / field_type / options / required).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { getJson } from '../../services/http';
import {
  archiveForm, embedFormUrl, FIELD_TYPES, getForm, hostedFormUrl, iframeSnippet, PURPOSES,
  rotateWebhookSecret, updateForm,
} from '../../services/formsApi';
import { notReadyMessage, purposeLabel } from './FormsPage';
import FormSubmissions from './FormSubmissions';
import PublicFormRenderer from './PublicFormRenderer';
import '../analytics/PerformancePanel.css';
import './Forms.css';

const TABS = ['build', 'settings', 'share', 'submissions'];
let uidSeq = 0;
const nextUid = () => `f${++uidSeq}`;

export function keyFromLabel(label, taken) {
  let base = String(label || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 40);
  if (!/^[a-z]/.test(base)) base = `field_${base}`.replace(/_+$/, '');
  let key = base;
  let n = 2;
  while (taken.has(key)) key = `${base}_${n++}`;
  return key;
}

function typeLabel(t, type) {
  const labels = {
    text: t('forms.type.text', 'Short text'),
    textarea: t('forms.type.textarea', 'Long text'),
    select: t('forms.type.select', 'Choice'),
    checkbox: t('forms.type.checkbox', 'Checkbox'),
    date: t('forms.type.date', 'Date'),
    phone: t('forms.type.phone', 'Phone'),
    email: t('forms.type.email', 'Email'),
  };
  return labels[type] || type;
}

function toDraft(f) {
  return {
    name: f.name || '',
    purpose: f.purpose,
    target: f.targetType === 'event' && f.targetId ? `event:${f.targetId}` : 'hub',
    title: f.title || '',
    intro: f.intro || '',
    submitLabel: f.submitLabel || '',
    consentText: f.consentText || '',
    emailOptInDefault: !!f.emailOptInDefault,
    pushPrompt: !!f.pushPrompt,
    successMessage: f.successMessage || '',
    redirectUrl: f.redirectUrl || '',
    showOnEventPage: !!f.showOnEventPage,
    webhookUrl: f.webhookUrl || '',
    active: !!f.active,
    fields: (f.fields || []).map((x) => ({ ...x, optionsText: (x.options || []).join('\n'), uid: nextUid() })),
  };
}

function fromDraft(d) {
  const [targetType, targetId] = d.target.split(':');
  return {
    name: d.name,
    purpose: d.purpose,
    targetType,
    targetId: targetId ? Number(targetId) : undefined,
    title: d.title,
    intro: d.intro,
    submitLabel: d.submitLabel,
    consentText: d.consentText,
    emailOptInDefault: d.emailOptInDefault,
    pushPrompt: d.pushPrompt,
    successMessage: d.successMessage,
    redirectUrl: d.redirectUrl,
    showOnEventPage: d.showOnEventPage,
    webhookUrl: d.webhookUrl,
    active: d.active,
    fields: d.fields.map((f) => ({
      field_key: f.field_key,
      label: f.label,
      field_type: f.field_type,
      required: !!f.required,
      options: f.field_type === 'select' ? f.optionsText.split('\n').map((o) => o.trim()).filter(Boolean) : undefined,
    })),
  };
}

export default function FormEditor({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();
  const tenant = Number(tenantProp || params.tenant);
  const formId = Number(params.formId);

  const [form, setForm] = useState(null);
  const [draft, setDraft] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('build');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const res = await getForm(tenant, formId);
      setForm(res.data);
      setDraft(toDraft(res.data));
      setTenantSlug(res.tenantSlug || null);
    } catch (err) {
      setError(notReadyMessage(t, err)
        || (/^404/.test(err.message) ? t('forms.notFound', 'Form not found.') : t('forms.loadError', 'Could not load forms.')));
    }
  }, [tenant, formId, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getJson(`/api/events/${tenant}/events`)
      .then((d) => setEvents(d.data || d.events || []))
      .catch(() => setEvents([]));
  }, [tenant]);

  const dirty = useMemo(
    () => !!(form && draft && JSON.stringify(fromDraft(draft)) !== JSON.stringify(fromDraft(toDraft(form)))),
    [form, draft],
  );

  const save = async () => {
    setSaving(true);
    setNotice(null);
    try {
      const res = await updateForm(tenant, formId, fromDraft(draft));
      setForm(res.data);
      setDraft(toDraft(res.data));
      setNotice({ kind: 'ok', text: t('forms.saved', 'Saved.') });
    } catch (err) {
      setNotice({ kind: 'error', text: err?.response?.data?.error || t('forms.saveError', 'Could not save the form.') });
    } finally {
      setSaving(false);
    }
  };

  const archive = async () => {
    if (!window.confirm(t('forms.archiveConfirm', 'Archive this form? It stops taking submissions. Existing submissions are kept.'))) return;
    try {
      await archiveForm(tenant, formId);
      navigate(`/${tenant}/forms`);
    } catch {
      setNotice({ kind: 'error', text: t('forms.archiveError', 'Could not archive the form.') });
    }
  };

  const footer = (
    <AppFooter
      buttons={[
        { icon: <FaArrowLeft />, label: t('forms.title', 'Forms'), onClick: () => navigate(`/${tenant}/forms`) },
        { icon: <FaHome />, label: t('common.home', 'Home'), onClick: () => navigate(`/${tenant}/dashboard`) },
      ]}
    />
  );

  if (error) return <div className="fo-page"><ErrorState message={error} onRetry={load} />{footer}</div>;
  if (!form || !draft) return <div className="fo-page"><LoadingState />{footer}</div>;

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const previewForm = {
    id: form.id,
    title: draft.title || draft.name,
    intro: draft.intro,
    fields: fromDraft(draft).fields,
    submitLabel: draft.submitLabel,
    consentText: draft.consentText,
    emailOptInDefault: draft.emailOptInDefault,
    pushPrompt: draft.pushPrompt,
  };

  return (
    <div className="fo-page">
      <section className="surface">
        <div className="fo-inline" style={{ justifyContent: 'space-between' }}>
          <div>
            <h2>{form.name}</h2>
            <p className="surface-muted perf-small">
              {purposeLabel(t, form.purpose)} · {form.targetType === 'event' ? (form.eventName || `#${form.targetId}`) : t('forms.target.hubShort', 'Public page')}
              {' · '}{form.active ? t('forms.status.open', 'Open') : t('forms.status.closed', 'Closed')}
            </p>
          </div>
          <div className="fo-inline">
            <button type="button" className="lm-btn" onClick={save} disabled={saving || !dirty}>
              {saving ? t('forms.saving', 'Saving…') : t('forms.save', 'Save')}
            </button>
            <button type="button" className="lm-btn secondary" onClick={archive}>{t('forms.archive', 'Archive')}</button>
          </div>
        </div>
        {notice && <div className={`note ${notice.kind}`} role={notice.kind === 'error' ? 'alert' : 'status'}>{notice.text}</div>}
      </section>

      <div className="fo-tabs" role="tablist">
        {TABS.map((k) => (
          <button key={k} type="button" role="tab" className="fo-tab" aria-selected={tab === k} onClick={() => setTab(k)}>
            {{
              build: t('forms.tab.build', 'Build'),
              settings: t('forms.tab.settings', 'Settings'),
              share: t('forms.tab.share', 'Share'),
              submissions: t('forms.tab.submissions', 'Submissions'),
            }[k]}
          </button>
        ))}
      </div>

      {tab === 'build' && (
        <div className="fo-grid split">
          <FieldBuilder fields={draft.fields} onChange={(fields) => set({ fields })} />
          <section className="surface">
            <h4>{t('forms.preview.title', 'Preview')}</h4>
            <div className="fo-preview">
              <PublicFormRenderer key={JSON.stringify(previewForm.fields.map((f) => f.field_key))} form={previewForm} preview />
            </div>
          </section>
        </div>
      )}

      {tab === 'settings' && <SettingsPanel draft={draft} set={set} events={events} />}

      {tab === 'share' && (
        <SharePanel
          form={form}
          draft={draft}
          set={set}
          tenant={tenant}
          tenantSlug={tenantSlug}
          onSecret={(webhookSecret) => setForm((f) => ({ ...f, webhookSecret }))}
        />
      )}

      {tab === 'submissions' && <FormSubmissions tenant={tenant} formId={formId} />}

      {footer}
    </div>
  );
}

function FieldBuilder({ fields, onChange }) {
  const { t } = useTranslation();
  const update = (i, patch) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  const remove = (i) => onChange(fields.filter((_, j) => j !== i));
  const add = () => {
    const taken = new Set(fields.map((f) => f.field_key));
    const label = t('forms.builder.newField', 'New question');
    onChange([...fields, { field_key: keyFromLabel(label, taken), label, field_type: 'text', required: false, optionsText: '', isNew: true, uid: nextUid() }]);
  };
  const emailCount = fields.filter((f) => f.field_type === 'email').length;

  return (
    <section className="surface">
      <h4>{t('forms.builder.title', 'Fields')}</h4>
      <p className="surface-muted perf-small">
        {t('forms.builder.help', 'The first email field becomes the contact\'s email and is always required. Use the arrows to reorder.')}
      </p>
      {emailCount === 0 && <div className="note warn">{t('forms.builder.needEmail', 'Add an email field before saving.')}</div>}
      <ol className="fo-fields">
        {fields.map((f, i) => (
          <li key={f.uid} className="fo-row">
            <label className="fo-field">
              {t('forms.builder.label', 'Label')}
              <input
                value={f.label}
                maxLength={150}
                onChange={(e) => {
                  const patch = { label: e.target.value };
                  if (f.isNew) {
                    const taken = new Set(fields.filter((_, j) => j !== i).map((x) => x.field_key));
                    patch.field_key = keyFromLabel(e.target.value || 'field', taken);
                  }
                  update(i, patch);
                }}
              />
            </label>
            <label className="fo-field">
              {t('forms.builder.type', 'Type')}
              <select value={f.field_type} onChange={(e) => update(i, { field_type: e.target.value })}>
                {FIELD_TYPES.map((ty) => <option key={ty} value={ty}>{typeLabel(t, ty)}</option>)}
              </select>
            </label>
            <label className="fo-check">
              <input type="checkbox" checked={!!f.required} onChange={(e) => update(i, { required: e.target.checked })} />
              {t('forms.builder.required', 'Required')}
            </label>
            <div className="fo-row-actions">
              <button type="button" className="lm-btn secondary" onClick={() => move(i, -1)} disabled={i === 0}
                aria-label={t('forms.builder.moveUp', 'Move {{label}} up', { label: f.label })}>↑</button>
              <button type="button" className="lm-btn secondary" onClick={() => move(i, 1)} disabled={i === fields.length - 1}
                aria-label={t('forms.builder.moveDown', 'Move {{label}} down', { label: f.label })}>↓</button>
              <button type="button" className="lm-btn secondary" onClick={() => remove(i)}
                aria-label={t('forms.builder.remove', 'Remove {{label}}', { label: f.label })}>✕</button>
            </div>
            {f.field_type === 'select' && (
              <label className="fo-field fo-options">
                {t('forms.builder.options', 'Choices (one per line)')}
                <textarea rows={3} value={f.optionsText || ''} onChange={(e) => update(i, { optionsText: e.target.value })} />
              </label>
            )}
            <span className="surface-muted perf-small fo-options">{t('forms.builder.key', 'Key')}: <code>{f.field_key}</code></span>
          </li>
        ))}
      </ol>
      <p><button type="button" className="lm-btn secondary" onClick={add} disabled={fields.length >= 30}>{t('forms.builder.add', 'Add field')}</button></p>
    </section>
  );
}

function SettingsPanel({ draft, set, events }) {
  const { t } = useTranslation();
  const text = (key, label, opts = {}) => (
    <label className="fo-field">
      {label}
      {opts.multiline
        ? <textarea rows={3} maxLength={opts.max} value={draft[key]} onChange={(e) => set({ [key]: e.target.value })} placeholder={opts.placeholder} />
        : <input value={draft[key]} maxLength={opts.max} onChange={(e) => set({ [key]: e.target.value })} placeholder={opts.placeholder} />}
    </label>
  );
  return (
    <section className="surface">
      <div className="fo-form-grid">
        {text('name', t('forms.field.name', 'Name (staff only)'), { max: 150 })}
        <label className="fo-field">
          {t('forms.field.purpose', 'Purpose')}
          <select value={draft.purpose} onChange={(e) => set({ purpose: e.target.value })}>
            {PURPOSES.map((p) => <option key={p} value={p}>{purposeLabel(t, p)}</option>)}
          </select>
        </label>
        <label className="fo-field">
          {t('forms.field.attachTo', 'Attach to')}
          <select value={draft.target} onChange={(e) => set({ target: e.target.value })}>
            <option value="hub">{t('forms.target.hub', 'Public page (not one event)')}</option>
            {events.map((ev) => <option key={ev.id} value={`event:${ev.id}`}>{ev.name}</option>)}
          </select>
        </label>
        {text('title', t('forms.field.heading', 'Heading'), { max: 200 })}
        {text('submitLabel', t('forms.field.submitLabel', 'Button text'), { max: 60, placeholder: t('forms.public.submit', 'Submit') })}
      </div>
      {text('intro', t('forms.field.intro', 'Intro text'), { multiline: true, max: 5000 })}
      <div className="fo-form-grid">
        {text('consentText', t('forms.field.consent', 'Email consent checkbox text (empty hides it)'), { max: 500 })}
        <label className="fo-check">
          <input type="checkbox" checked={draft.emailOptInDefault} onChange={(e) => set({ emailOptInDefault: e.target.checked })} />
          {t('forms.field.optInDefault', 'Consent box starts ticked')}
        </label>
        <label className="fo-check">
          <input type="checkbox" checked={draft.pushPrompt} onChange={(e) => set({ pushPrompt: e.target.checked })} />
          {t('forms.field.pushPrompt', 'Offer browser notifications after submit')}
        </label>
        <label className="fo-check">
          <input type="checkbox" checked={draft.active} onChange={(e) => set({ active: e.target.checked })} />
          {t('forms.field.active', 'Taking submissions')}
        </label>
      </div>
      <div className="fo-form-grid">
        {text('successMessage', t('forms.field.success', 'Message after submit'), { max: 500, placeholder: t('forms.field.successDefault', 'Thanks. You are on the list.') })}
        {text('redirectUrl', t('forms.field.redirect', 'Or send people to this URL after submit'), { max: 500, placeholder: 'https://' })}
      </div>
      <p className="surface-muted perf-small">{t('forms.saveHint', 'Changes apply after Save.')}</p>
    </section>
  );
}

function CopyRow({ label, value }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* ignore */ }
  };
  return (
    <div className="fo-field">
      <span>{label}</span>
      <code className="fo-code">{value}</code>
      <div className="fo-inline">
        <button type="button" className="lm-btn secondary" onClick={copy}>{copied ? t('forms.copied', 'Copied') : t('forms.copy', 'Copy')}</button>
      </div>
    </div>
  );
}

function SharePanel({ form, draft, set, tenant, tenantSlug, onSecret }) {
  const { t } = useTranslation();
  const [rotating, setRotating] = useState(false);
  const rotate = async () => {
    if (!window.confirm(t('forms.webhook.rotateConfirm', 'Make a new signing secret? The old one stops working right away.'))) return;
    setRotating(true);
    try {
      const res = await rotateWebhookSecret(tenant, form.id);
      onSecret(res.data.webhookSecret);
    } finally {
      setRotating(false);
    }
  };
  return (
    <div className="fo-grid">
      <section className="surface">
        <h4>{t('forms.share.title', 'Share and embed')}</h4>
        {!tenantSlug && <div className="note warn">{t('forms.share.noSlug', 'Set your organization\'s public link name in Settings to get share links.')}</div>}
        {!form.active && <div className="note warn">{t('forms.share.closed', 'This form is closed. Links show "closed" until you turn submissions back on.')}</div>}
        {tenantSlug && (
          <>
            <CopyRow label={t('forms.share.hosted', 'Form page link')} value={hostedFormUrl(tenantSlug, form.id)} />
            <CopyRow label={t('forms.share.iframe', 'Embed on your website (iframe)')} value={iframeSnippet(tenantSlug, form.id, form.title || form.name)} />
            <p className="surface-muted perf-small">
              {t('forms.share.embedNote', 'The embed shows only the form, at {{url}}.', { url: embedFormUrl(tenantSlug, form.id) })}
            </p>
          </>
        )}
        {draft.target.startsWith('event:') && (
          <label className="fo-check">
            <input type="checkbox" checked={draft.showOnEventPage} onChange={(e) => set({ showOnEventPage: e.target.checked })} />
            {t('forms.share.eventPage', 'Show this form on the event\'s public page')}
          </label>
        )}
      </section>

      <section className="surface">
        <h4>{t('forms.webhook.title', 'Webhook')}</h4>
        <p className="surface-muted perf-small">
          {t('forms.webhook.help', 'Each submission is POSTed as JSON to this https URL. Failed deliveries retry with backoff for about a day.')}
        </p>
        <label className="fo-field">
          {t('forms.webhook.url', 'Webhook URL')}
          <input value={draft.webhookUrl} maxLength={500} placeholder="https://" onChange={(e) => set({ webhookUrl: e.target.value })} />
        </label>
        {form.webhookUrl && form.webhookSecret && (
          <>
            <CopyRow label={t('forms.webhook.secret', 'Signing secret')} value={form.webhookSecret} />
            <div className="note info">
              {t('forms.webhook.verify', 'Verify the X-OnScreen-Signature header: it is "t=<timestamp>,v1=<hex>", where hex is HMAC-SHA256 of "<timestamp>.<raw body>" with this secret.')}
            </div>
            <p><button type="button" className="lm-btn secondary" onClick={rotate} disabled={rotating}>{t('forms.webhook.rotate', 'New secret')}</button></p>
          </>
        )}
        <p className="surface-muted perf-small">{t('forms.saveHint', 'Changes apply after Save.')}</p>
      </section>
    </div>
  );
}
